import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ExchangeAddBlockedDialog } from 'components/exchange/exchange-add-blocked-dialog';
import { useAppState } from 'lib/app-state';
import { getChangeTicketsHref } from 'lib/helpers/exchange-helpers';
import { useCancelExchange } from 'lib/hooks/use-cancel-exchange';

/**
 * TODO(NEWBUY-1069): temporary. The exchange backend cannot take extra items
 * on an exchange order — there is no endpoint to add a ticket, donation, gift
 * card or membership to it — so every add is blocked while a change is in
 * progress. Remove this guard, its dialog and every `guardAdd` call site once
 * adding to an exchange order is supported server-side.
 *
 * A change runs through the ordinary cart on a staging order of its own, so the
 * regular "add to cart" paths would post straight onto it. Rather than hide the
 * buttons, the add is intercepted at the moment it fires and the buyer chooses:
 * finish the change, or drop it so the new tickets or items open a fresh order.
 */
type ExchangeAddGuardValue = {
  /** Runs `add` unless a ticket change is in progress; otherwise holds it and
   *  opens the dialog, running it only if the change is cancelled. */
  guardAdd: (add: () => void) => void;
  /** Whether a ticket change is in progress. */
  isExchangeInProgress: boolean;
};
const ExchangeAddGuardContext = createContext<ExchangeAddGuardValue>({
  guardAdd: add => add(),
  isExchangeInProgress: false
});
export const useExchangeAddGuard = () => useContext(ExchangeAddGuardContext);
export const ExchangeAddGuardProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const router = useRouter();
  const {
    tempExchangeData
  } = useAppState();
  const [pendingAdd, setPendingAdd] = useState<(() => void) | null>(null);
  // Set when the buyer drops the change, so the held add runs once the exchange
  // has actually left app state rather than against the staging order.
  const [runAfterCancel, setRunAfterCancel] = useState(false);
  const isExchangeInProgress = !!tempExchangeData;
  const guardAdd = useCallback((add: () => void) => {
    if (!tempExchangeData) {
      add();
      return;
    }
    setPendingAdd(() => add);
  }, [tempExchangeData]);
  const closeDialog = useCallback(() => setPendingAdd(null), []);
  const continueExchange = useCallback(() => {
    setPendingAdd(null);
    if (!tempExchangeData) return;
    // Started changes carry on in the cart; one still choosing a new event goes
    // back to that step.
    router.push(tempExchangeData.exchangeOrderId ? '/cart' : getChangeTicketsHref(tempExchangeData.sourceEncryptedOrderId, tempExchangeData.sourceEventId, tempExchangeData.subscriptionTypeId));
  }, [router, tempExchangeData]);

  // Before a staging order exists the order in state is the buyer's own, and
  // the held add belongs in it — cancelling leaves it alone.
  const dropExchange = useCancelExchange();
  const cancelExchange = useCallback(() => {
    dropExchange();
    setRunAfterCancel(true);
  }, [dropExchange]);

  // The add reads the active order when it fires, so it has to wait for the
  // exchange to clear — running it inline would still post to the staging order.
  useEffect(() => {
    if (!runAfterCancel || tempExchangeData) return;
    setRunAfterCancel(false);
    setPendingAdd(null);
    pendingAdd?.();
  }, [pendingAdd, runAfterCancel, tempExchangeData]);
  const value = useMemo(() => ({
    guardAdd,
    isExchangeInProgress
  }), [guardAdd, isExchangeInProgress]);
  return <ExchangeAddGuardContext.Provider value={value}>
      {children}
      <ExchangeAddBlockedDialog open={!!pendingAdd && !runAfterCancel} onClose={closeDialog} onContinueExchange={continueExchange} onCancelExchange={cancelExchange} />
    </ExchangeAddGuardContext.Provider>;
};